/**
 * Helpers for maintaining {@link SyncMeta} on entities (decisions #9/#20).
 * The storage layer calls these on every write so rev/updatedAt/hash stay
 * consistent, which is what makes manifest-diff sync and conflict detection
 * work.
 */
import type { Entity, SyncMeta } from "../domain/common.js";
import { hashString, stableStringify } from "./hash.js";

/** A short, stable id for this replica/device (for conflict attribution). */
function getOrCreateOrigin(get: () => string | undefined, set: (v: string) => void): string {
  let origin = get();
  if (!origin) {
    origin = Math.random().toString(36).slice(2, 10);
    set(origin);
  }
  return origin;
}

/** Fresh sync metadata for a brand-new entity. */
export function newSyncMeta(origin?: string): SyncMeta {
  const now = Date.now();
  return {
    rev: 1,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    ...(origin ? { origin } : {}),
  };
}

/**
 * Produce the next sync metadata for an updated entity: bump rev, refresh
 * updatedAt, and recompute the content hash over the entity payload (excluding
 * the `sync` field itself) so equal content yields an equal hash (decision #20).
 */
export async function bumpSyncMeta<T extends Entity>(
  entity: T,
  origin?: string,
): Promise<SyncMeta> {
  const { sync, ...payload } = entity;
  const hash = await hashString(stableStringify(payload));
  return {
    rev: (sync?.rev ?? 0) + 1,
    createdAt: sync?.createdAt ?? Date.now(),
    updatedAt: Date.now(),
    hash,
    deletedAt: sync?.deletedAt ?? null,
    ...(origin ? { origin } : {}),
  };
}

/** Mark an entity's metadata as a tombstone (soft delete) for sync (#20). */
export function tombstone(sync: SyncMeta, origin?: string): SyncMeta {
  return {
    ...sync,
    rev: sync.rev + 1,
    updatedAt: Date.now(),
    deletedAt: Date.now(),
    ...(origin ? { origin } : {}),
  };
}

export { getOrCreateOrigin };
